import { useEffect, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import Select from 'react-select';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { useMessages } from '../../contexts/messages';
import { useMessageTemplate } from "../../hooks/useMessageTemplate";
import { EMPTY_EDITOR_DESIGN } from "@Constants/html-templates";
import { useUserRoles } from '@Hooks/useUserRoles';
import AiSnippetPickerModal from "../AiSnippetPickerModal";
import service from '../../service';

const DEFAULT_METHOD_OPTIONS = [
  { value: 'SMS', label: 'SMS' },
  { value: 'EMAIL', label: 'EMAIL' },
  { value: 'PUSH', label: 'PUSH' },
];

// "Send to user" mixes two sources: trigger-data user fields (inbound SMS's inbox
// owner) and attached inbox types (an activity's stylist, lead owner, …).
const USER_FIELD_SOURCE = 'field:';
const ATTACHED_TYPE_SOURCE = 'type:';

const TriggerOutboundMessageForm = ({ filteredMergeTags, showHTMLEditor, setShowHTMLEditor, handleReloadEditor, exportEmail, stageTypeId }) => {
  const [allUserRoles, setAllUserRoles] = useState([]);
  const [activeField, setActiveField] = useState(null);
  const [aiSnippetModalOpen, setAiSnippetModalOpen] = useState(false);

  const { data: userRolesPayload, userRolesStatus } = useUserRoles({
    searchParams: {},
  });

  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitted },
  } = useFormContext();

  const {
    inboxes,
    fetchAllInboxes,
    fetchAllUsers,
    userList,
  } = useMessages();

  const method = watch('actions.type');
  const recipientType = watch('actions.recipientType');
  const hasSender = watch('actions.senderId');
  const userField = watch('actions.userField');
  const recipientInboxType = watch('actions.recipientInboxType');
  const hasRecipients =
    !!userField ||
    !!recipientInboxType ||
    watch('actions.userRoleIds')?.length > 0 ||
    watch('actions.userIds')?.length > 0;

  const senderInboxType = watch('actions.senderInboxType');

  const { data: attachedInboxTypes = [] } = useQuery({
    queryKey: ['attached-inbox-types', stageTypeId],
    queryFn: async () => service.getAttachedInboxTypes(stageTypeId),
    enabled: !!stageTypeId,
  });

  const SPECIFIC_INBOX_VALUE = '';
  const senderSourceOptions = [
    ...attachedInboxTypes.map((t) => ({ value: t.type, label: `${t.label} Inbox` })),
    { value: SPECIFIC_INBOX_VALUE, label: 'Specific inbox' },
  ];

  const { data: userFields = [] } = useQuery({
    queryKey: ['user-fields', stageTypeId],
    queryFn: async () => service.getUserFields(stageTypeId),
    enabled: !!stageTypeId,
  });

  const SPECIFIC_USERS_VALUE = '';
  const userSourceOptions = [
    ...userFields.map((f) => ({ value: `${USER_FIELD_SOURCE}${f.field}`, label: f.label })),
    ...attachedInboxTypes.map((t) => ({ value: `${ATTACHED_TYPE_SOURCE}${t.type}`, label: t.label })),
    { value: SPECIFIC_USERS_VALUE, label: 'Specific users' },
  ];
  const userSource = userField
    ? `${USER_FIELD_SOURCE}${userField}`
    : recipientInboxType
      ? `${ATTACHED_TYPE_SOURCE}${recipientInboxType}`
      : SPECIFIC_USERS_VALUE;

  const { data: payload, status } = useMessageTemplate({
    searchParams: {
      '[filter][method]': method,
      quickMessage: false,
    }
  });

  const mergeTagOptions = filteredMergeTags.map((tag) => ({
    value: `[[${tag.id}]]`,
    label: tag.label,
  }));

  const messageTemplateOptions = payload?.data?.map((template) => {
    let designJson = EMPTY_EDITOR_DESIGN;
    if (template.htmlEditorJson && Object.keys(template.htmlEditorJson).length > 0) {
      designJson = template.htmlEditorJson;
    }
    return {
      id: template.id,
      value: template.messageBody,
      subject: template.messageSubject,
      html: template.html ?? null,
      htmlEditorJson: designJson,
      method: template.method,
      label: template.name,
    };
  });
  const userRolesOptions = allUserRoles?.map((userRole) => ({
    value: userRole.id,
    label: userRole.name,
  }));

  const userOptions = userList.map((user) => ({
    value: user.id,
    label: `${user.name || ''} (${(method === 'EMAIL' ? user.email : user.phoneNumber) || 'No email'})`,
  }));

  const filteredInboxes = inboxes.filter((inbox) => {
    if (inbox.fishId === null) { //exclude fish inboxes
      if (method === 'SMS') return inbox.phoneNumber;
      if (method === 'EMAIL') return inbox.email;
      return true;
    }
    return false;
  });

  const inboxOptions = filteredInboxes.map((inbox) => ({
    value: inbox.id,
    label: `${method === 'SMS' ? inbox.phoneNumber : inbox.email} ${inbox.user?.fullDisplayName ? `(${inbox.user?.fullDisplayName})` : ''}`,
  }));

  const handleTemplateChange = (selected) => {
    if (!selected) return;
    setValue("actions.body", selected.value);
    setValue("actions.subject", selected.subject);
    setValue("actions.html", selected.html)
    setValue("actions.htmlEditorJson", selected.htmlEditorJson)

    handleReloadEditor(selected);
  };

  const handleMergeTagChange = (selected) => {
    if (!selected) return;


    if (activeField === 'body') {
      const messageBody = watch('actions.body') || '';
      const textArea = document.querySelector('textarea[name="actions.body"]');

      const start = textArea.selectionStart;
      const end = textArea.selectionEnd;

      const newValue =
        messageBody.substring(0, start) +
        selected.value +
        messageBody.substring(end);

      setValue('actions.body', newValue);

      setTimeout(() => {
        textArea.focus();
        textArea.setSelectionRange(
          start + selected.value.length,
          start + selected.value.length,
        );
      }, 0);
    } else if (activeField === 'subject') {
      const messageSubject = watch('actions.subject') || '';
      const input = document.querySelector('input[name="actions.subject"]');
      const start = input.selectionStart;
      const end = input.selectionEnd;

      const newValue =
        messageSubject.substring(0, start) +
        selected.value +
        messageSubject.substring(end);

      setValue('actions.subject', newValue);

      setTimeout(() => {
        input.focus();
        input.setSelectionRange(
          start + selected.value.length,
          start + selected.value.length,
        );
      }, 0);
    } else {
      const messageBody = watch('actions.body') || '';
      setValue('actions.body', messageBody + selected.value);
    }
  };

  useEffect(() => {
    if (recipientType === 'fish') {
      setValue('actions.fishIds', ['fish']);
      setValue('actions.userIds', []);
      setValue('actions.userField', undefined);
      setValue('actions.recipientInboxType', undefined);
      setValue('actions.userRoleIds', []);
    } else {
      const current = getValues('actions.fishIds');
      if (Array.isArray(current) && current.includes('fish')) {
        setValue(
          'actions.fishIds',
          current.filter((val) => val !== 'fish'),
        );
      }
    }
  }, [recipientType]);

  useEffect(() => {
    fetchAllInboxes();
    fetchAllUsers();
  }, []);

  useEffect(() => {
    if (method === 'EMAIL') {
      if (getValues('actions.body') && !getValues('actions.html')) {
        setShowHTMLEditor(false)
      } else {
        setShowHTMLEditor(true)
      }
    }
  }, [method]);

  useEffect(() => {
    if (userRolesPayload) {
      setAllUserRoles(userRolesPayload.data)
    }
  }, [userRolesPayload])

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-500">
          Method:
        </label>
        <Controller
          name="actions.type"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              value={
                value
                  ? { value, label: value }
                  : null
              }
              onChange={(selected) => {
                onChange(selected?.value || '')
                if (selected?.value === 'PUSH') {
                  setValue(
                    'actions.recipientType',
                    'fish',
                  )
                }
                if (selected?.value !== 'EMAIL') {
                  setShowHTMLEditor(false);
                }
              }}
              options={DEFAULT_METHOD_OPTIONS}
              placeholder="Select method..."
              classNamePrefix="select"
            />
          )}
        />
      </div>

      {method !== 'PUSH' && <div>
        {attachedInboxTypes.length > 0 && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-500">
              Send from:
            </label>
            <Select
              value={
                senderSourceOptions.find(
                  (option) => option.value === (senderInboxType || SPECIFIC_INBOX_VALUE),
                ) || null
              }
              onChange={(selected) => {
                const next = selected?.value || SPECIFIC_INBOX_VALUE;
                setValue(
                  'actions.senderInboxType',
                  next === SPECIFIC_INBOX_VALUE ? undefined : next,
                );
              }}
              options={senderSourceOptions}
              placeholder="Select source..."
              classNamePrefix="select"
            />
          </div>
        )}
        <label className="block text-sm font-medium text-gray-500">
          {senderInboxType ? 'Fallback inbox:' : 'From (Sender):'}
        </label>
        <Controller
          name="actions.senderId"
          control={control}
          rules={{ required: 'Sender is required' }}
          render={({ field: { value, onChange } }) => (
            <Select
              value={
                inboxOptions.find((option) => option.value === value) || null
              }
              onChange={(selected) => onChange(selected?.value || '')}
              options={inboxOptions}
              placeholder="Select sender inbox..."
              classNamePrefix="select"
              className={errors.actions?.senderId ? 'border-red-500' : ''}
            />
          )}
        />
        {errors.actions?.senderId && (
          <p className="text-sm text-red-500">
            {errors.actions.senderId.message}
          </p>
        )}
      </div>}

      <div>

        {method !== 'PUSH' && (
          <>
            <label className="mb-1 block text-sm font-medium text-gray-500">
              Send To:
            </label>
            <div className="mb-2 flex items-center gap-4">
              <div className="flex w-10 justify-center">
                <span
                  className={
                    recipientType === 'fish'
                      ? 'font-bold text-blue-600'
                      : 'text-gray-400'
                  }
                >
                  Fish
                </span>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={recipientType === 'internal'}
                  onChange={() =>
                    setValue(
                      'actions.recipientType',
                      recipientType === 'fish' ? 'internal' : 'fish',
                    )
                  }
                />
                <div className="peer h-6 w-11 rounded-full bg-gray-300 peer-focus:outline-none after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
              </label>
              <div className="flex w-20 justify-center">
                <span
                  className={
                    recipientType === 'internal'
                      ? 'font-bold text-blue-600'
                      : 'text-gray-400'
                  }
                >
                  Internal
                </span>
              </div>
            </div>
          </>
        )}

        {recipientType === 'internal' && (
          <>
            {userSourceOptions.length > 1 && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-500">
                  Send to user:
                </label>
                <Select
                  value={
                    userSourceOptions.find((option) => option.value === userSource) || null
                  }
                  onChange={(selected) => {
                    const next = selected?.value || SPECIFIC_USERS_VALUE;
                    setValue(
                      'actions.userField',
                      next.startsWith(USER_FIELD_SOURCE)
                        ? next.slice(USER_FIELD_SOURCE.length)
                        : undefined,
                    );
                    setValue(
                      'actions.recipientInboxType',
                      next.startsWith(ATTACHED_TYPE_SOURCE)
                        ? next.slice(ATTACHED_TYPE_SOURCE.length)
                        : undefined,
                    );
                  }}
                  options={userSourceOptions}
                  placeholder="Select source..."
                  classNamePrefix="select"
                />
              </div>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-500">
                {userField || recipientInboxType ? 'Fallback users:' : 'Internal Users:'}
              </label>
              <Controller
                name="actions.userIds"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    isMulti
                    value={
                      Array.isArray(value)
                        ? value.map((id) => {
                          const user = userList.find((u) => u.id === id);
                          return {
                            value: id,
                            label: user
                              ? `${user.name || ''} (${(method === 'EMAIL' ? user.email : user.phoneNumber) || 'No email'})`
                              : id,
                          };
                        })
                        : []
                    }
                    onChange={(selected) =>
                      onChange([...selected.map((option) => option.value)])
                    }
                    options={userOptions}
                    classNamePrefix="select"
                    placeholder="Select internal users..."
                  />
                )}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-500">
                User Roles:
              </label>
              <Controller
                name="actions.userRoleIds"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    isMulti
                    value={
                      Array.isArray(value)
                        ? value.map((id) => {
                          const userRole = userRoles.find(
                            (ug) => ug.id === id,
                          );
                          return { value: id, label: userRole?.name || id };
                        })
                        : []
                    }
                    onChange={(selected) =>
                      onChange(selected?.map((option) => option.value) || [])
                    }
                    options={userRolesOptions}
                    classNamePrefix="select"
                    placeholder="Select user roles..."
                  />
                )}
              />
            </div>
          </>
        )}
      </div>

      {method === 'EMAIL' &&
        <div className="mt-4 mb-2 flex items-center gap-4">
          <div className="flex w-fit justify-center">
            <span
              className={
                showHTMLEditor === false
                  ? 'font-bold text-blue-600'
                  : 'text-gray-400'
              }
            >
              Message
            </span>
          </div>
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={showHTMLEditor === true}
              onChange={async () => {
                if (showHTMLEditor) {
                  await exportEmail();
                }
                setShowHTMLEditor(!showHTMLEditor);
              }}
            />
            <div className="peer h-6 w-11 rounded-full bg-gray-300 peer-focus:outline-none after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
          </label>
          <div className="flex w-fit justify-center">
            <span
              className={
                showHTMLEditor === true
                  ? 'font-bold text-blue-600'
                  : 'text-gray-400'
              }
            >
              HTML
            </span>
          </div>
        </div>
      }

      <div>
        <div className="flex items-center justify-between">
          {method !== 'SMS' && (
            <label className="block text-sm font-medium text-gray-500">
              Message Subject:
            </label>
          )}
          <div className="flex gap-2">
            <Select
              options={mergeTagOptions}
              onChange={handleMergeTagChange}
              placeholder="Insert Merge Tag"
              isClearable
              className="w-48"
              classNamePrefix="select"
              value={null}
            />

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAiSnippetModalOpen(true)}
            >
              Insert AI Snippet
            </Button>

            <AiSnippetPickerModal
              open={aiSnippetModalOpen}
              onOpenChange={setAiSnippetModalOpen}
              onInsert={(tag) => handleMergeTagChange({ value: tag })}
            />

            <Select
              options={messageTemplateOptions}
              onChange={handleTemplateChange}
              placeholder="Templates"
              isClearable
              className="w-48"
              classNamePrefix="select"
              value={null}
            />
          </div>
        </div>
        {method !== 'SMS' && (
          <Controller
            name="actions.subject"
            control={control}
            render={({ field }) => (
              <input
                onFocus={() => setActiveField('subject')}
                type="text"
                {...field}
                className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1 shadow-sm"
                placeholder="Message subject..."
              />
            )}
          />
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-500">
          Message Body:
        </label>

        {!showHTMLEditor && (
          <Controller
            name="actions.body"
            control={control}
            rules={{ required: 'Message body is required' }}
            render={({ field }) => (
              <textarea
                onFocus={() => setActiveField('body')}
                rows="5"
                {...field}
                className={`mt-1 block w-full rounded-md border border-gray-300 p-px px-2 shadow-sm py-1${
                  errors.actions?.body ? 'border-red-500' : ''
                }`}
                placeholder="Message body..."
              />
            )}
          />
        )}
        {errors.actions?.body && (
          <p className="text-sm text-red-500">{errors.actions.body.message}</p>
        )}
      </div>

      {isSubmitted && recipientType !== 'fish' && !hasRecipients && (
        <p className="text-center text-sm text-red-500">
          At least one recipient (User or User Group) is required
        </p>
      )}
      {isSubmitted && recipientType !== 'fish' && !hasSender && (
        <p className="text-center text-sm text-red-500">
          A sender inbox must be selected
        </p>
      )}
    </div>
  );
};

export default TriggerOutboundMessageForm;